import { useState } from 'react';
import { Phone } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { PhotoGrid } from '../../components/common/PhotoGrid';
import { MoneyInput } from '../../components/common/MoneyInput';
import { formatMoney } from '../../lib/money';
import { PAYMENT_METHODS } from '../../data/enums';
import { BOOKING_STATUSES, PAYMENT_STATUSES, STATUS_FLOW } from './config';
import type { BookingRow } from './hooks';

// record_booking_payment RPC method whitelist is cash|card|etransfer.
// Filter cheque/other out of the method dropdown — they would be rejected at
// the RPC. Filter at consumer site per D7; do not modify shared PAYMENT_METHODS.
const BOOKING_PAYMENT_METHODS = PAYMENT_METHODS.filter(m =>
  m.value === 'cash' || m.value === 'card' || m.value === 'etransfer'
);

interface Props {
  booking: BookingRow | null;
  showPhotos?: boolean;
  onClose: () => void;
  onAdvance: (id: string, status: string) => void;
  onRecordPayment: (id: string, method: string, amount: number, tip: number) => Promise<boolean>;
  onCloseWithoutPayment: (id: string) => Promise<boolean>;
}

function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

type PendingAction = 'cancel' | 'no_show' | 'close_unpaid' | null;

export function BookingDetailPanel({ booking, showPhotos, onClose, onAdvance, onRecordPayment, onCloseWithoutPayment }: Props) {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [showPay, setShowPay] = useState(false);
  const [payMethod, setPayMethod] = useState('cash');
  const [payAmount, setPayAmount] = useState('');
  const [payTip, setPayTip] = useState('');
  const [paying, setPaying] = useState(false);

  if (!booking) return null;
  const st = BOOKING_STATUSES[booking.status] ?? BOOKING_STATUSES.pending;
  const ps = PAYMENT_STATUSES[(booking.payment_status as string) ?? 'unpaid'] ?? PAYMENT_STATUSES.unpaid;
  const isActive = STATUS_FLOW.includes(booking.status) && booking.status !== 'completed';
  const nextIdx = STATUS_FLOW.indexOf(booking.status) + 1;
  const nextStatus = nextIdx > 0 && nextIdx < STATUS_FLOW.length ? STATUS_FLOW[nextIdx] : null;

  const handlePay = async () => {
    setPaying(true);
    // Q1.5 boundary: onRecordPayment accepts numbers; payAmount/payTip are canonical after MoneyInput blur.
    const ok = await onRecordPayment(booking.id, payMethod, Number(payAmount) || booking.payment_amount || 0, Number(payTip) || 0);
    setPaying(false);
    if (ok) setShowPay(false);
  };

  return (
    <SlidePanel isOpen={!!booking} onClose={onClose} title={booking.client_name ?? 'Booking'} subtitle={`${booking.date} · ${fmtTime(booking.start_time)} – ${fmtTime(booking.end_time)}`}>
      {/* Status + Payment badges */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: st.bg, color: st.fg }}>{st.label}</span>
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500, background: ps.bg, color: ps.fg }}>{ps.label}</span>
        {isActive && nextStatus && (
          <button onClick={() => onAdvance(booking.id, nextStatus)} style={{
            marginLeft: 'auto', padding: '5px 14px', borderRadius: 6, border: 'none',
            background: palette.accent, color: palette.white, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Mark {BOOKING_STATUSES[nextStatus]?.label}</button>
        )}
      </div>

      {/* Status pipeline */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {STATUS_FLOW.map(s => (
          <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: STATUS_FLOW.indexOf(booking.status) >= STATUS_FLOW.indexOf(s) ? palette.accent : palette.g200 }} />
        ))}
      </div>

      {/* Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20, fontSize: 13 }}>
        <div>
          <span style={{ color: palette.g400 }}>Client</span>
          <div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{booking.client_name ?? '—'}</div>
          {booking.client_phone && (
            <a href={`tel:${booking.client_phone}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: palette.accent, marginTop: 2, textDecoration: 'none' }}>
              <Phone size={11} /> {booking.client_phone}
            </a>
          )}
        </div>
        <div><span style={{ color: palette.g400 }}>Service</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{booking.service_name ?? '—'}</div></div>
        <div><span style={{ color: palette.g400 }}>Assigned</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{booking.assigned_name ?? 'Unassigned'}</div></div>
        <div><span style={{ color: palette.g400 }}>Source</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2, textTransform: 'capitalize' }}>{booking.source?.replace('_', ' ') ?? '—'}</div></div>
        {booking.payment_amount != null && (
          <div><span style={{ color: palette.g400 }}>Amount</span><div style={{ fontWeight: 600, color: palette.g800, marginTop: 2 }}>{formatMoney(booking.payment_amount)}</div></div>
        )}
      </div>
      {booking.notes && <div style={{ fontSize: 13, color: palette.g600, padding: '10px 12px', background: palette.g50, borderRadius: 6, marginBottom: 16 }}>{booking.notes}</div>}

      {/* Photos (Cluster A/F only) */}
      {showPhotos && <PhotoGrid entityType="booking" entityId={booking.id} tableName="booking_photos" foreignKey="booking_id" phases={['before', 'after']} />}

      {/* Payment section. Gate is "still owes / not yet collected" per D14:
          unpaid (open) and prepaid (partially collected) are payable; closed_unpaid
          is terminal and must not show payment CTAs. */}
      <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: palette.g700 }}>Payment</span>
          {(booking.payment_status === 'unpaid' || booking.payment_status === 'prepaid') && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setPendingAction('close_unpaid')} style={{
                padding: '5px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white,
                color: palette.g600, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>No payment received</button>
              <button onClick={() => { setShowPay(true); setPayAmount(String(booking.payment_amount ?? '')); }} style={{
                padding: '5px 12px', borderRadius: 6, border: 'none', background: palette.green,
                color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}>Record Payment</button>
            </div>
          )}
        </div>
        {showPay && (
          <div style={{ padding: 12, background: palette.g50, borderRadius: 8, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <select value={payMethod} onChange={e => setPayMethod(e.target.value)} style={{ padding: '8px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit' }}>
              {BOOKING_PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <MoneyInput value={payAmount} onChange={setPayAmount} placeholder="Amount" style={{ width: 110 }} />
            <MoneyInput value={payTip} onChange={setPayTip} placeholder="Tip" style={{ width: 90 }} />
            <button onClick={handlePay} disabled={paying} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: palette.green, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{paying ? '...' : 'Record'}</button>
          </div>
        )}
      </div>

      {/* Cancel / No-show actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {isActive && <button onClick={() => setPendingAction('cancel')} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'transparent', fontSize: 13, color: palette.red, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>}
          {isActive && <button onClick={() => setPendingAction('no_show')} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${palette.red}`, background: palette.white, fontSize: 13, color: palette.red, cursor: 'pointer', fontFamily: 'inherit' }}>No Show</button>}
        </div>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
      </div>

      <ConfirmDialog
        isOpen={pendingAction !== null}
        title={
          pendingAction === 'cancel' ? 'Cancel this booking?'
          : pendingAction === 'no_show' ? 'Mark as no-show?'
          : pendingAction === 'close_unpaid' ? 'Close without payment?'
          : ''
        }
        message={
          pendingAction === 'cancel'
            ? 'The booking will be marked cancelled and the slot reopens. The client is not notified automatically.'
            : pendingAction === 'no_show'
            ? 'The booking will be marked as no-show and the slot reopens. The client is not notified automatically.'
            : pendingAction === 'close_unpaid'
            ? 'The booking will be closed with no payment recorded. Use this when work was done but no payment will be collected. Nothing posts to income.'
            : ''
        }
        confirmLabel={
          pendingAction === 'cancel' ? 'Cancel booking'
          : pendingAction === 'no_show' ? 'Mark no-show'
          : pendingAction === 'close_unpaid' ? 'Close without payment'
          : 'Confirm'
        }
        confirmVariant={pendingAction === 'close_unpaid' ? 'warning' : 'danger'}
        onConfirm={async () => {
          if (pendingAction === 'cancel') onAdvance(booking.id, 'cancelled');
          else if (pendingAction === 'no_show') onAdvance(booking.id, 'no_show');
          else if (pendingAction === 'close_unpaid') await onCloseWithoutPayment(booking.id);
          setPendingAction(null);
          onClose();
        }}
        onClose={() => setPendingAction(null)}
      />
    </SlidePanel>
  );
}
